import { Amplify, Auth } from 'aws-amplify';
import { CognitoUser, CognitoAccessToken } from 'amazon-cognito-identity-js';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SIGN_IN } from '../common/paths';
import { AmplifyConfigAuth } from '../config/auth';
import { AmplifyConfigAPI } from '../config/api';

Amplify.configure({
    Auth: AmplifyConfigAuth,
    API: AmplifyConfigAPI,
});

interface UseAuth {
    isLoading: boolean;
    isAuthenticated: boolean;
    isAdministrator: boolean;
    isSuperAdmin: boolean;
    isUser: boolean;
    user: CognitoUser;
    currentClientName: string;
    currentClientCode: string;
    signIn: (username: string, password: string) => Promise<Result>;
    signOut: () => void;
    setNewPassword: (session: any, password: string, requiredAttributes: any) => Promise<Result>;
    getUser: () => Promise<any>;
    getAccessToken: () => Promise<CognitoAccessToken>;
    setClient: (code : string, name : string) => void;
}

interface Result {
    success: boolean;
    message: string;
    insufficientPermissions?: boolean;
    passwordChangeRequired?: boolean;
    session?: any;
}

type Props = {
    children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const AuthProvider: React.FC<Props> = ({ children }) => {
    const auth = useProvideAuth();
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const signInPath = `/${SIGN_IN}`;
        if (((!auth.isLoading && !auth.isAuthenticated) || (auth.isAuthenticated && !auth.isSuperAdmin && !auth.isAdministrator && !auth.isUser)) && location.pathname !== signInPath) {
            console.info('user not logged in... navigating to:', signInPath)
            navigate(signInPath, { state: location, replace: true });
        }
    }, [location, auth.isAuthenticated, auth.isLoading, navigate, auth.isAdministrator, auth.isSuperAdmin, auth.isUser])
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
    return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdministrator, setIsAdministrator] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isUser, setIsUser] = useState(false);
    const [user, setUser] = useState<any>();
    const [currentClientName, setCurrentClientName] = useState('');
    const [currentClientCode, setCurrentClientCode] = useState('');

    const setClient = useCallback((code : string, name : string) => {
        setCurrentClientCode(code);
        setCurrentClientName(name);
    }, []);

    const checkPermissions = useCallback(async (): Promise<boolean> => {
        const accessToken = await getAccessToken();

        if (accessToken !== undefined) {
            const groupList = accessToken.payload['cognito:groups'] ?? [];

            setIsAdministrator(false);
            setIsUser(false);

            let inAdminGroup = false;
            let inSuperAdminGroup = false;
            let inUserGroup = false;

            groupList.forEach((group: string) => {
                if (group === 'Administrator')
                    inAdminGroup = true;

                if (group === 'User')
                    inUserGroup = true;

                if (group === 'SuperAdministrator')
                    inSuperAdminGroup = true;
            });

            setIsAdministrator(inAdminGroup);
            setIsSuperAdmin(inSuperAdminGroup);
            setIsUser(inUserGroup);

            return inAdminGroup || inSuperAdminGroup || inUserGroup;
        }

        return false;
    }, []);

    useEffect(() => {
        Auth.currentAuthenticatedUser()
            .then((result) => {
                setUser(result);
                setIsAuthenticated(true);
                checkPermissions().finally(() => setIsLoading(false));
            })
            .catch(() => {
                setUser(undefined);
                setIsAuthenticated(false);
                setIsLoading(false);
                setIsAdministrator(false);
                setIsUser(false);
            });
    }, [checkPermissions]);

    const signIn = async (username: string, password: string) => {
        try {
            setIsLoading(true);
            const result = await Auth.signIn(username, password);
            setUser(result);

            if (!result.challengeName) {
                const hasPermissions = await checkPermissions();

                if (!hasPermissions) {
                    setIsLoading(false);
                    return {
                        success: false,
                        insufficientPermissions: true,
                        message: 'Insufficient permissions'
                    }
                }
            }


            const challenge = result.challengeName;
            if (challenge) {
                switch (challenge) {
                    case 'NEW_PASSWORD_REQUIRED': {
                        setIsLoading(false);
                        return { success: false, passwordChangeRequired: true, session: result, message: '' };
                    }
                    default: {
                        console.error(`Challenge ${challenge} not implemented.`);
                        return {
                            success: false,
                            message: `Sing in failed. Challenge ${challenge} not implemented.`,
                        };
                    }
                }
            }

            setIsAuthenticated(true);
            setIsLoading(false);
            return { success: true, message: '' };
        } catch (error: any) {
            setIsLoading(false);
            console.error(error);
            return {
                success: false,
                message: error.message || 'Sign in failed',
            };
        }
    };

    const setNewPassword = async (session: any, password: string, requiredAttributes: any) => {
        try {
            setIsLoading(true);
            const result = await Auth.completeNewPassword(session, password, requiredAttributes);
            setUser(result);
            setIsAuthenticated(true);
            setIsLoading(false);

            const hasPermissions = await checkPermissions();

            if (!hasPermissions) {
                setIsLoading(false);
                return {
                    success: false,
                    insufficientPermissions: true,
                    message: 'Insufficient permissions'
                }
            } else
                return { success: true, message: '' };
        } catch (error: any) {
            setIsLoading(false);
            console.error(error);
            return {
                success: false,
                message: error.message || 'Failed to change password',
            };
        }
    }

    const signOut = async () => {
        try {
            setIsLoading(true);
            await Auth.signOut();
            setUser(undefined);
            setIsAuthenticated(false);
            setIsLoading(false);
            setIsAdministrator(false);
            setIsSuperAdmin(false);
            setIsUser(false);
            return { success: true, message: '' };
        } catch (error) {
            setIsLoading(false);
            console.error(error);
            return {
                success: false,
                message: 'Sign out failed',
            };
        }
    };

    const getUser = async () => {
        return await Auth.currentUserInfo();
    };;

    const getAccessToken = async (): Promise<CognitoAccessToken> => {
        const session = await Auth.currentSession();
        return await session.getAccessToken();
    };

    return {
        isLoading,
        isAuthenticated,
        user,
        isAdministrator,
        isSuperAdmin,
        isUser,
        currentClientName,
        currentClientCode,
        signIn,
        signOut,
        setNewPassword,
        getUser,
        getAccessToken,
        setClient
    };
};
